import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import debounce from "lodash/debounce";
import { confirm } from "../common/Confirm/confirm";
import { toast } from '../common/Toast/toast';
import {
  INVALID_SCHEDULE_MESSAGE,
  isPastSchedule,
  PAST_SCHEDULE_MESSAGE,
  toEventDateTime,
  todayInputValue,
} from '../../../utils/eventSchedule';

import { useAuth } from "../../../context/AuthContext";
import { getMe } from "../../../services/AuthService";
import { getTrainers } from "../../../services/TrainerService";
import { getBatches, getBatchesByTrainers } from "../../../services/BatchService";
import {
  createMockInterview,
  deleteMockInterview,
  getMockInterviewEventStats,
  getMockInterviews,
  updateMockInterview,
} from "../../../services/MockInterviewListService";
import NewCustomDataTable from "../common/NewCustomDataTable.jsx";
import "../Trainer/TrainerDashboard.css";
import "../common/lms-pages.css";
import MockInterviewDetailModal from "./MockInterviewDetailModal.jsx";
import PopupDone, { usePopupDone } from "../common/PopupDone/PopupDone.jsx";
import NewCustomAsyncSelectDropdown from "../common/NewCustomAsyncSelectDropdown.jsx";
import { getPriorityMeta, normalizePriority, priorityOptions } from "../../../utils/eventPriority";
import {
  getStatusMeta,
  normalizeStatus,
  statusOptions,
  STATUS_ACTIVE,
} from "../../../utils/eventStatus";
import FilterSheet from "../common/FilterSheet/FilterSheet";
import useDebouncedCallback from "../common/useDebouncedCallback";

// The module this page is gated on. Admins/superadmins pass every check in
// AuthContext.hasPermission regardless; everyone else needs an `lms` module
// with exactly this value on one of their profiles.
const PERMISSION_MODULE = "mockinterviewlist";

// The trainer/batch pickers pull their options a page at a time; 100 is the
// API's own per-request cap, so this is one round trip per page of options.
const OPTION_PAGE_SIZE = 100;

// The filter dropdowns page as you scroll, so they start small.
const FILTER_PAGE_SIZE = 20;

// Typing in a picker refetches from the server rather than filtering the 100
// already loaded — without the wait that would be a request per keystroke.
const SEARCH_DEBOUNCE_MS = 350;

const emptyForm = {
  id: null,
  name: "",
  date: "",
  time: "",
  trainerIds: [],
  batchIds: [],
  status: STATUS_ACTIVE,
  priority: "Not set",
  onlineLink: "",
  offlineAddress: "",
  description: "",
};

const MockInterviews = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const canView = hasPermission(PERMISSION_MODULE, "view", "lms");
  const canAdd = hasPermission(PERMISSION_MODULE, "add", "lms");
  const canUpdate = hasPermission(PERMISSION_MODULE, "update", "lms");
  const canDelete = hasPermission(PERMISSION_MODULE, "delete", "lms");

  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uiSearchTerm, setUiSearchTerm] = useState("");

  // Read before the fetch awaits anything: a reset mid-flight would turn an
  // append into a replace and snap the list back to its first page.
  const appendRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const listEndRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("name");
  const [loading, setLoading] = useState(true);

  // ── Filter panel ────────────────────────────────────────────────────────
  // Selections are staged until "Apply Filters" so that changing three
  // dropdowns costs one request, not three.
  const [selectedFilterTrainers, setSelectedFilterTrainers] = useState([]);
  const [selectedFilterBatches, setSelectedFilterBatches] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  // Turnout and result figures per event, keyed by event id.
  const [eventStats, setEventStats] = useState({});
  // The mock interview shown in the view popup, or null when it is closed.
  const [viewing, setViewing] = useState(null);
  const [appliedTrainerFilter, setAppliedTrainerFilter] = useState("");
  const [appliedBatchFilter, setAppliedBatchFilter] = useState("");
  const [appliedStatusFilter, setAppliedStatusFilter] = useState("");

  const [filterTrainerOptions, setFilterTrainerOptions] = useState([]);
  const [filterBatchOptions, setFilterBatchOptions] = useState([]);
  const [filterTrainersLoading, setFilterTrainersLoading] = useState(false);
  const [filterBatchesLoading, setFilterBatchesLoading] = useState(false);

  const [currentUser, setCurrentUser] = useState(null);
  const [userReady, setUserReady] = useState(false);

  const [trainerOptions, setTrainerOptions] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [loadingTrainers, setLoadingTrainers] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const hasShownPermissionToast = useRef(false);
  const hasLoadedUser = useRef(false);
  // Remembers id -> label for everything ever loaded or opened for edit, so a
  // saved trainer/batch that isn't on the current page of options still shows
  // its name instead of a raw ObjectId chip.
  const labelCache = useRef({ trainer: new Map(), batch: new Map() });
  const trainerSearchTimer = useRef(null);
  const batchSearchTimer = useRef(null);
  const filterTrainerPaging = useRef({ page: 1, search: "", hasMore: true });
  const filterBatchPaging = useRef({ page: 1, search: "", hasMore: true });

  const isEditing = Boolean(form.id);

  // ── Access guard ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!canView && !hasShownPermissionToast.current) {
      hasShownPermissionToast.current = true;
      toast.error("You do not have permission to view Mock Interviews.");
      navigate("/");
    }
  }, [canView, navigate]);

  useEffect(
    () => () => {
      clearTimeout(trainerSearchTimer.current);
      clearTimeout(batchSearchTimer.current);
    },
    []
  );

  // ── Current user (decides whether a trainer is scoped to their own) ───────
  useEffect(() => {
    if (hasLoadedUser.current) return;
    hasLoadedUser.current = true;
    (async () => {
      try {
        const response = await getMe();
        setCurrentUser(response?.data || null);
      } catch {
        setCurrentUser(null);
      } finally {
        setUserReady(true);
      }
    })();
  }, []);

  const profileLabel = String(currentUser?.profiles?.[0]?.label || "").toLowerCase();
  const isAdminUser =
    Boolean(currentUser?.isSuperAdmin) || profileLabel === "admin" || profileLabel === "crm";
  const isTrainerUser = profileLabel === "trainer";

  // ── List ──────────────────────────────────────────────────────────────────
  const fetchRows = useCallback(async () => {
    // A scroll batch keeps the rows on screen and carries its own spinner in
    // the foot; raising `loading` for it would veil the whole table on every
    // batch, over rows the reader is part-way through.
    const isAppendRun = appendRef.current;
    if (isAppendRun) setLoadingMore(true);
    else setLoading(true);
    try {
      // A plain trainer manages only the mock interviews they're assigned to,
      // so their own id wins over anything the trainer filter says; admins and
      // CRM see every one and filter freely.
      const ownScope =
        !isAdminUser && isTrainerUser && currentUser?.id ? String(currentUser.id) : null;

      const isAppend = appendRef.current;
      const res = await getMockInterviews({
        limit: recordsPerPage,
        page: currentPage,
        search: searchTerm,
        searchField,
        // Both are CSVs of ids — the API matches an event naming ANY of them.
        trainerId: ownScope || appliedTrainerFilter || null,
        batchId: appliedBatchFilter || null,
        status: appliedStatusFilter || null,
      });

      const pageData = Array.isArray(res?.data) ? res.data : [];
      const pageOffset = (currentPage - 1) * recordsPerPage;

      setTotalRecords(Number(res?.pagination?.total || 0));
      const mapped = pageData.map((item, index) => ({
          ...item,
          srno: pageOffset + index + 1,
          dateDisplay: formatDateDisplay(item.date),
          timeDisplay: formatTimeDisplay(item.time),
          trainerNames: toNameList(item.trainerId, "trainerName"),
          batchNames: toNameList(item.batchId, "name"),
          statusValue: normalizeStatus(item.status),
          // The cells render components; these plain mirrors are what the
          // table's CSV export reads.
          attendedDisplay: "",
          performanceDisplay: "",
          priorityValue: normalizePriority(item.priority),
          createdAtDisplay: formatDateTimeDisplay(item.createdAt),
          updatedAtDisplay: formatDateTimeDisplay(item.updatedAt),
      }));

      // Guarded by id so a repeated page cannot double up a row.
      if (isAppend) {
        setRows((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...mapped.filter((r) => !seen.has(r.id))];
        });
      } else {
        setRows(mapped);
      }
      appendRef.current = false;
      // Stats are fetched for just the page in view, right after it renders,
      // so a slow roll-up never holds the table back.
      const ids = pageData.map((item) => item.id).filter(Boolean);
      if (ids.length) {
        getMockInterviewEventStats(ids).then((stats) => {
          setEventStats(stats);
          // Mirror the figures onto the rows so the CSV export and the
          // table's own text extraction carry them too.
          setRows((prev) =>
            prev.map((row) => {
              const stat = stats[row.id];
              if (!stat) return row;
              return {
                ...row,
                attendedDisplay: `${stat.attended} of ${stat.expected}`,
                performanceDisplay:
                  stat.performanceRate === null ? "Not evaluated" : `${stat.performanceRate}%`,
              };
            })
          );
        });
      } else {
        setEventStats({});
      }
    } catch (error) {
      console.error("Error fetching mock interviews:", error);
      toast.error("Failed to fetch mock interviews");
      appendRef.current = false;
      setRows([]);
      setTotalRecords(0);
      setEventStats({});
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [
    currentPage,
    recordsPerPage,
    searchTerm,
    searchField,
    appliedTrainerFilter,
    appliedBatchFilter,
    appliedStatusFilter,
    isAdminUser,
    isTrainerUser,
    currentUser,
  ]);

  useEffect(() => {
    if (canView && userReady) fetchRows();
  }, [canView, userReady, fetchRows]);

  // ── Filter dropdowns ──────────────────────────────────────────────────────
  //
  // These are separate from the modal's pickers on purpose: the modal's batch
  // list is scoped to the trainers being assigned to that one record, while
  // these describe what the user wants to see in the table.

  const fetchFilterTrainers = useCallback(async (page = 1, search = "", append = false) => {
    setFilterTrainersLoading(true);
    try {
      const res = await getTrainers(FILTER_PAGE_SIZE, page, search);
      const list = (Array.isArray(res?.data) ? res.data : [])
        .filter((t) => t?.id)
        .map((t) => ({
          value: String(t.id),
          label: t.trainerName || t.trainerEmail || String(t.id),
        }));
      setFilterTrainerOptions((prev) => (append ? mergeOptions(prev, list) : list));
      filterTrainerPaging.current = {
        page,
        search,
        hasMore: hasNextPage(res?.pagination, list.length),
      };
    } catch (error) {
      console.error("Error loading trainer filter options:", error);
      if (!append) setFilterTrainerOptions([]);
    } finally {
      setFilterTrainersLoading(false);
    }
  }, []);

  // With trainers picked, only their batches are offered — filtering by a
  // trainer and a batch they don't teach can only ever return nothing.
  // `trainerCsvOverride` lets the trainer-change handler pass the new
  // selection before its state update has landed.
  const fetchFilterBatches = useCallback(
    async (page = 1, search = "", append = false, trainerCsvOverride) => {
      const trainerCsv =
        trainerCsvOverride !== undefined
          ? trainerCsvOverride
          : selectedFilterTrainers.map((o) => o.value).join(",");
      const trainerIds = trainerCsv ? trainerCsv.split(",").filter(Boolean) : [];

      setFilterBatchesLoading(true);
      try {
        const res = trainerIds.length
          ? await getBatchesByTrainers(trainerIds, FILTER_PAGE_SIZE, page, search, "name")
          : await getBatches(FILTER_PAGE_SIZE, page, search, "name");

        // The two endpoints wrap their rows differently.
        const raw = res?.data?.configuedBatches || res?.data || [];
        const list = (Array.isArray(raw) ? raw : [])
          .filter((b) => b?.id)
          .map((b) => ({ value: String(b.id), label: b.name || String(b.id) }));

        setFilterBatchOptions((prev) => (append ? mergeOptions(prev, list) : list));
        filterBatchPaging.current = {
          page,
          search,
          hasMore: hasNextPage(res?.data?.pagination || res?.pagination, list.length),
        };
      } catch (error) {
        console.error("Error loading batch filter options:", error);
        if (!append) setFilterBatchOptions([]);
      } finally {
        setFilterBatchesLoading(false);
      }
    },
    [selectedFilterTrainers]
  );

  const debouncedFilterTrainerSearch = useMemo(
    () => debounce((value) => fetchFilterTrainers(1, value, false), SEARCH_DEBOUNCE_MS),
    [fetchFilterTrainers]
  );
  const debouncedFilterBatchSearch = useMemo(
    () => debounce((value) => fetchFilterBatches(1, value, false), SEARCH_DEBOUNCE_MS),
    [fetchFilterBatches]
  );

  useEffect(
    () => () => {
      debouncedFilterTrainerSearch.cancel();
      debouncedFilterBatchSearch.cancel();
    },
    [debouncedFilterTrainerSearch, debouncedFilterBatchSearch]
  );

  const handleFilterTrainerChange = (selected) => {
    const options = Array.isArray(selected) ? selected : [];
    setSelectedFilterTrainers(options);
    // The batch options are about to be rebuilt for these trainers, so any
    // batch chosen under the previous selection has to go with them.
    setSelectedFilterBatches([]);
    setFilterBatchOptions([]);
    filterBatchPaging.current = { page: 1, search: "", hasMore: true };
    fetchFilterBatches(1, "", false, options.map((o) => o.value).join(","));
  };

  const handleApplyFilters = () => {
    appendRef.current = false;
    setAppliedTrainerFilter(selectedFilterTrainers.map((o) => o.value).join(","));
    setAppliedBatchFilter(selectedFilterBatches.map((o) => o.value).join(","));
    setAppliedStatusFilter(statusFilter);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    appendRef.current = false;
    setSelectedFilterTrainers([]);
    setSelectedFilterBatches([]);
    setStatusFilter("");
    setAppliedTrainerFilter("");
    setAppliedBatchFilter("");
    setAppliedStatusFilter("");
    setFilterTrainerOptions([]);
    setFilterBatchOptions([]);
    filterTrainerPaging.current = { page: 1, search: "", hasMore: true };
    filterBatchPaging.current = { page: 1, search: "", hasMore: true };
    setCurrentPage(1);
  };

  const hasStagedFilters =
    selectedFilterTrainers.length > 0 || selectedFilterBatches.length > 0 || Boolean(statusFilter);
  const hasAppliedFilters =
    Boolean(appliedTrainerFilter) || Boolean(appliedBatchFilter) || Boolean(appliedStatusFilter);

  // ── Trainer options ───────────────────────────────────────────────────────
  const loadTrainers = useCallback(async (search = "") => {
    setLoadingTrainers(true);
    try {
      const res = await getTrainers(OPTION_PAGE_SIZE, 1, search);
      const list = Array.isArray(res?.data) ? res.data : [];
      const options = list
        .filter((t) => t?.id)
        .map((t) => ({
          value: String(t.id),
          label: t.trainerName || t.trainerEmail || String(t.id),
        }));
      options.forEach((o) => labelCache.current.trainer.set(o.value, o.label));
      setTrainerOptions(options);
    } catch (error) {
      console.error("Error loading trainers:", error);
      toast.error("Failed to load trainers");
      setTrainerOptions([]);
    } finally {
      setLoadingTrainers(false);
    }
  }, []);

  // ── Batch options, always scoped to the trainers currently picked ─────────
  //
  // `pruneSelection` is on only when the trainer selection itself changed:
  // dropping a trainer has to drop the batches only they taught, or the record
  // would be saved against a batch none of its trainers actually run. Opening
  // an existing record leaves its saved batches alone.
  const loadBatchesForTrainers = useCallback(
    async (trainerIds, { search = "", pruneSelection = false } = {}) => {
      const ids = (trainerIds || []).map((id) => String(id).trim()).filter(Boolean);
      if (!ids.length) {
        setBatchOptions([]);
        if (pruneSelection) setForm((prev) => ({ ...prev, batchIds: [] }));
        return;
      }
      setLoadingBatches(true);
      try {
        const res = await getBatchesByTrainers(ids, OPTION_PAGE_SIZE, 1, search, "name");
        const raw = res?.data?.configuedBatches || res?.data || [];
        const options = (Array.isArray(raw) ? raw : [])
          .filter((b) => b?.id)
          .map((b) => ({ value: String(b.id), label: b.name || String(b.id) }));
        options.forEach((o) => labelCache.current.batch.set(o.value, o.label));
        setBatchOptions(options);

        if (pruneSelection) {
          const allowed = new Set(options.map((o) => o.value));
          setForm((prev) => {
            const kept = prev.batchIds.filter((id) => allowed.has(String(id)));
            return kept.length === prev.batchIds.length ? prev : { ...prev, batchIds: kept };
          });
        }
      } catch (error) {
        console.error("Error loading batches:", error);
        toast.error("Failed to load batches");
        setBatchOptions([]);
      } finally {
        setLoadingBatches(false);
      }
    },
    []
  );

  // ── Form open/close ───────────────────────────────────────────────────────
  const openCreate = () => {
    // A trainer creating their own mock interview starts pre-selected, the
    // same way the event form behaves for trainer users.
    const ownTrainerIds =
      !isAdminUser && isTrainerUser && currentUser?.id ? [String(currentUser.id)] : [];

    setForm({ ...emptyForm, trainerIds: ownTrainerIds });
    setFormErrors({});
    setBatchOptions([]);
    setShowForm(true);
    loadTrainers();
    if (ownTrainerIds.length) loadBatchesForTrainers(ownTrainerIds);
  };

  const openEdit = (row) => {
    const trainerIds = toIdList(row.trainerId);
    const batchIds = toIdList(row.batchId);

    cacheLabels(labelCache.current.trainer, row.trainerId, "trainerName");
    cacheLabels(labelCache.current.batch, row.batchId, "name");

    setForm({
      id: row.id,
      name: row.name || "",
      date: toDateInput(row.date),
      time: toTimeInput(row.time),
      trainerIds,
      batchIds,
      status: normalizeStatus(row.status),
      priority: normalizePriority(row.priority),
      onlineLink: cleanOptional(row.onlineLink),
      offlineAddress: cleanOptional(row.offlineAddress),
      description: cleanOptional(row.description),
    });
    setFormErrors({});
    setShowForm(true);
    loadTrainers();
    loadBatchesForTrainers(trainerIds);
  };

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setForm(emptyForm);
    setFormErrors({});
  };

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const handleTrainerChange = (selected) => {
    const ids = (selected || []).map((o) => o.value);
    setForm((prev) => ({ ...prev, trainerIds: ids }));
    setFormErrors((prev) => ({ ...prev, trainerIds: undefined }));
    loadBatchesForTrainers(ids, { pruneSelection: true });
  };

  const handleTrainerInput = (value, meta) => {
    if (meta?.action !== "input-change") return;
    clearTimeout(trainerSearchTimer.current);
    trainerSearchTimer.current = setTimeout(() => loadTrainers(value), SEARCH_DEBOUNCE_MS);
  };

  const handleBatchInput = (value, meta) => {
    if (meta?.action !== "input-change") return;
    clearTimeout(batchSearchTimer.current);
    batchSearchTimer.current = setTimeout(
      () => loadBatchesForTrainers(form.trainerIds, { search: value }),
      SEARCH_DEBOUNCE_MS
    );
  };

  // Selected chips are built from the cache rather than from `options`, so a
  // selection stays visible (and named) while a different page of options or a
  // search result is loaded underneath it.
  // `*Options` are in the deps because the cache is a ref: it's refilled by
  // the same fetches that set the options, so those are what signal that a
  // label the chips are waiting on has arrived.
  const selectedTrainers = useMemo(
    () => toSelectValue(form.trainerIds, labelCache.current.trainer),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.trainerIds, trainerOptions]
  );
  const selectedBatches = useMemo(
    () => toSelectValue(form.batchIds, labelCache.current.batch),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.batchIds, batchOptions]
  );

  const validate = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = "Title is required";
    if (!form.date) errors.date = "Date is required";
    if (!form.time) errors.time = "Time is required";
    if (form.date && form.time && !toEventDateTime(form.date, form.time)) errors.time = INVALID_SCHEDULE_MESSAGE;
    // A new mock interview cannot be set for a time that has gone; an
    // existing one stays editable.
    if (!isEditing && isPastSchedule(form.date, form.time)) errors.time = PAST_SCHEDULE_MESSAGE;
    if (!form.trainerIds.length) errors.trainerIds = "Select at least one trainer";
    if (!form.batchIds.length) errors.batchIds = "Select at least one batch";
    if (!form.description.trim()) errors.description = "Description is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        ...toEventDateTime(form.date, form.time),
        // Stored as a UTC instant on the epoch date — the same convention the
        // event form uses, so the calendar reads both back identically.
        trainerId: form.trainerIds.join(","),
        batchId: form.batchIds.join(","),
        status: form.status,
        priority: form.priority,
        description: form.description.trim(),
        onlineLink: form.onlineLink.trim() || null,
        offlineAddress: form.offlineAddress.trim() || null,
      };

      if (isEditing) {
        await updateMockInterview(form.id, payload);
        toast.success("Mock interview updated successfully");
      } else {
        await createMockInterview(payload);
        toast.success("Mock interview created successfully");
      }

      // The dialog shows the save in place and closes itself; closeForm
      // empties the fields then.
      // The record changed, so the view popup still open behind this form
      // would be showing the old one. It goes; the form stays.
      setViewing(null);
      // A new record lands at the top of a descending list, so go back to page
      // 1 rather than leaving the user on a page that doesn't hold it.
      if (!isEditing && currentPage !== 1) setCurrentPage(1);
      else fetchRows();
    } catch (error) {
      const message =
        error?.response?.data?.message?.message ||
        error?.response?.data?.message ||
        `Failed to ${isEditing ? "update" : "create"} mock interview`;
      toast.error(typeof message === "string" ? message : "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const result = await confirm({
      title: "Delete this mock interview?",
      text: `"${row.name}" will be removed permanently.`,
      tone: 'danger',
      confirmButtonText: "Yes, delete it",
    });
    if (!result.isConfirmed) return;

    try {
      await deleteMockInterview(row.id);
      // The view popup says so in place, then closes itself.
      toast.success("Mock interview deleted");
      // Deleting the only row on the last page would otherwise leave the user
      // staring at an empty page.
      if (rows.length === 1 && currentPage > 1) setCurrentPage(currentPage - 1);
      else fetchRows();
    } catch (error) {
      console.error("Error deleting mock interview:", error);
      toast.error(error?.response?.data?.message || "Failed to delete mock interview");
    }
  };

  // Opens the record popup. The row and its turnout stats are already
  // loaded, so there is nothing to fetch: the popup draws a whole record.
  const handleView = (row) => setViewing(row);

  // A save in the form is confirmed in the form's own dialog, not a toast.
  const { done: formDone } = usePopupDone(showForm);

  // ── Table wiring ──────────────────────────────────────────────────────────
  /* Typing does not fire a request per keystroke any more -- the box and
     the field beside it still move at once, and the fetch underneath
     waits for the typing to stop. The field setter stays out here: the
     select is bound to searchField, so deferring it would leave the
     dropdown showing the old choice for as long as the delay lasts. */
  const runSearch = useDebouncedCallback((value) => {
    appendRef.current = false;
    setSearchTerm(value);
    setCurrentPage(1);
  });

  const onSearchChange = (value, field) => {
    setUiSearchTerm(value);
    if (field) setSearchField(field === "all" ? "name" : field);
    runSearch(value);
  };

  const hasMore = rows.length < totalRecords;

  // The foot of the list asks for the next page when it comes into view.
  useEffect(() => {
    const node = listEndRef.current;
    if (!node || !hasMore || loading) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((x) => x.isIntersecting)) return;
        // `loading` is state and lags a frame, so the ref is what stops one
        // pass asking for two pages.
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        appendRef.current = true;
        setLoadingMore(true);
        setCurrentPage((p) => p + 1);
      },
      { rootMargin: "150px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loading, rows.length]);

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      appendRef.current = false;
      setCurrentPage(page);
    }
  };

  const handleRowsPerPageChange = (size) => {
    appendRef.current = false;
    setRecordsPerPage(size);
    setCurrentPage(1);
  };

  const searchableFields = useMemo(
    () => [
      { value: "name", label: "Title" },
      { value: "description", label: "Description" },
      { value: "status", label: "Status" },
      { value: "priority", label: "Priority" },
    ],
    []
  );

  const headers = [
    {
      title: "Interview",
      prop: "name",
      sortable: true,
      cell: (row) => (
        <div className="lp-cell">
          <span className="lp-name">{row.name}</span>
          <span className="lp-sub">
            {row.trainerNames?.length
              ? row.trainerNames.slice(0, 2).join(", ")
              : "No trainer"}
            {row.trainerNames?.length > 2 && ` +${row.trainerNames.length - 2}`}
          </span>
        </div>
      ),
    },
    {
      title: "When",
      prop: "dateDisplay",
      sortable: true,
      cell: (row) => (
        <div className="lp-cell">
          <span className="lp-when">{row.dateDisplay || "—"}</span>
          {row.timeDisplay && (
            <span className="lp-sub">{row.timeDisplay}</span>
          )}
        </div>
      ),
    },
    {
      title: "Batches",
      prop: "batchNames",
      cell: (row) => <NameList names={row.batchNames} />,
    },
    {
      title: "Attended",
      prop: "attendedDisplay",
      cell: (row) => <AttendedCell stat={eventStats[row.id]} />,
    },
    {
      title: "Score",
      prop: "performanceDisplay",
      cell: (row) => <PerformanceCell stat={eventStats[row.id]} />,
    },
    {
      title: "Status",
      prop: "statusValue",
      sortable: true,
      cell: (row) => <Pill meta={getStatusMeta(row.statusValue)} />,
    },
  ];

  return (
    <div className="pg lp">

      <header className="pg-head pg-head--bare">
        <div className="pg-head-actions">
          {canAdd && (
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={openCreate}
            >
              Add mock interview
            </button>
          )}
        </div>
      </header>

      <FilterSheet label="Filter mock interviews">
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="mi-search">
              Search
            </label>
            <input
              id="mi-search"
              type="search"
              className="pg-input pg-input--search"
              placeholder="Search by interview title..."
              value={uiSearchTerm}
              onChange={(e) => onSearchChange(e.target.value, searchField)}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="mi-field">
              Search in
            </label>
            <select
              id="mi-field"
              className="pg-select"
              value={searchField}
              /* A discrete choice, not typing -- no reason to make it
                 wait out the search delay. */
              onChange={(e) => {
                onSearchChange(uiSearchTerm, e.target.value);
                runSearch.flush(uiSearchTerm);
              }}
            >
              {searchableFields.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="mi-rows">
              Rows per load
            </label>
            <select
              id="mi-rows"
              className="pg-select"
              value={recordsPerPage}
              onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        <div className="pg-filters-row">
          <div className="pg-filters-fields pg-filters-fields--notrainer">
            {/* A trainer only ever sees their own mock interviews, so a
                trainer picker would be a dropdown with one usable value. */}
            {(!isTrainerUser || isAdminUser) && (
              <div className="pg-field">
                <label className="pg-label">Trainers</label>
                <NewCustomAsyncSelectDropdown
                  options={filterTrainerOptions}
                  value={selectedFilterTrainers}
                  onChange={handleFilterTrainerChange}
                  placeholder="All trainers"
                  isMulti
                  isSearchable
                  searchInControl
                  isLoading={filterTrainersLoading}
                  onMenuOpen={() => fetchFilterTrainers(1, "", false)}
                  onInputChange={debouncedFilterTrainerSearch}
                  onMenuScrollToBottom={() => {
                    const { page, search, hasMore } = filterTrainerPaging.current;
                    if (hasMore && !filterTrainersLoading) {
                      fetchFilterTrainers(page + 1, search, true);
                    }
                  }}
                  onRefresh={() => fetchFilterTrainers(1, "", false)}
                  noOptionsMessage="No trainers found"
                />
              </div>
            )}

            <div className="pg-field">
              <label className="pg-label">Batches</label>
              <NewCustomAsyncSelectDropdown
                options={filterBatchOptions}
                value={selectedFilterBatches}
                onChange={(selected) =>
                  setSelectedFilterBatches(Array.isArray(selected) ? selected : [])
                }
                placeholder="All batches"
                isMulti
                isSearchable
                searchInControl
                isLoading={filterBatchesLoading}
                onMenuOpen={() => fetchFilterBatches(1, "", false)}
                onInputChange={debouncedFilterBatchSearch}
                onMenuScrollToBottom={() => {
                  const { page, search, hasMore } = filterBatchPaging.current;
                  if (hasMore && !filterBatchesLoading) {
                    fetchFilterBatches(page + 1, search, true);
                  }
                }}
                onRefresh={() => fetchFilterBatches(1, "", false)}
                noOptionsMessage={
                  selectedFilterTrainers.length
                    ? "No batches for the selected trainers"
                    : "No batches found"
                }
              />
            </div>

            <div className="pg-field pg-field--tight">
              <label className="pg-label" htmlFor="mi-status">
                Status
              </label>
              <select
                id="mi-status"
                className="pg-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All</option>
                {statusOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="pg-filters-actions">
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={handleApplyFilters}
              disabled={!hasStagedFilters && !hasAppliedFilters}
            >
              Apply
            </button>
            <button
              type="button"
              className="pg-btn"
              onClick={handleClearFilters}
              disabled={!hasStagedFilters && !hasAppliedFilters}
            >
              Clear
            </button>
          </div>
        </div>
      </FilterSheet>

      <div className="td-list td-list--lms">
        <NewCustomDataTable
          data={rows}
          headers={headers}
          totalRecords={totalRecords}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={handlePageChange}
          serverSide
          loading={loading}
          loadingMore={loadingMore}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => handleView(row)}
          validRowClickTagNames="*"
        />
      </div>

      <div className="att-picklist-foot" ref={listEndRef}>
        {loadingMore ? (
          <>
            <span className="pg-spinner att-spinner" aria-hidden="true" />
            <span>Loading more interviews...</span>
          </>
        ) : hasMore ? (
          <span>
            {rows.length} of {totalRecords} interviews
          </span>
        ) : totalRecords > 0 ? (
          <span>
            All {totalRecords} interview{totalRecords === 1 ? "" : "s"} loaded
          </span>
        ) : (
          !loading && <span>No mock interview matches those filters.</span>
        )}
      </div>

      {viewing && (
        <MockInterviewDetailModal
          row={viewing}
          stat={eventStats[viewing.id]}
          canEdit={canUpdate}
          canDelete={canDelete}
          onClose={() => setViewing(null)}
          // The popup stays behind the form or the confirm it hands off to.
          onEdit={() => {
            const record = viewing;
            openEdit(record);
          }}
          onDelete={() => {
            const record = viewing;
            handleDelete(record);
          }}
        />
      )}

      {showForm && (
        <div
          className="pg-modal pg-modal--over"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mi-form-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeForm();
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--wide pg-modal-panel--form">
        <header className="pg-modal-head">
          <div className="pg-modal-heading">
            <span className="pg-modal-eyebrow">Mock interview</span>
            <h3 className="pg-modal-title" id="mi-form-title">
              {isEditing ? "Edit mock interview" : "Add a mock interview"}
            </h3>
            <p className="pg-modal-sub">
              What it is, who sits it, and when.
            </p>
          </div>
          <button
            type="button"
            className="pg-modal-close"
            onClick={closeForm}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        <div className="pg-modal-body">
          {formDone ? (
            <PopupDone message={formDone} onClose={closeForm} />
          ) : (
          <div className="att-step">
            <div className="mcq-options">
            <div className="pg-field">
              <label className="pg-label">
                Title
              </label>
              <input
                className="pg-input"
                type="text"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="e.g. React Round 1 — Mock Interview"
              />
              {formErrors.name && (
                <p className="att-error">{formErrors.name}</p>
              )}
            </div>

            <div className="pg-field">
              <label className="pg-label">
                Date
              </label>
              <input
                className="pg-input"
                type="date"
                value={form.date}
                min={isEditing ? undefined : todayInputValue()}
                onChange={(e) => setField("date", e.target.value)}
              />
              {formErrors.date && (
                <p className="att-error">{formErrors.date}</p>
              )}
            </div>

            <div className="pg-field">
              <label className="pg-label">
                Time
              </label>
              <input
                className="pg-input"
                type="time"
                value={form.time}
                onChange={(e) => setField("time", e.target.value)}
              />
              {formErrors.time && (
                <p className="att-error">{formErrors.time}</p>
              )}
            </div>

            <div className="pg-field">
              <label className="pg-label">
                Trainers
              </label>
              <Select
                isMulti
                options={trainerOptions}
                value={selectedTrainers}
                onChange={handleTrainerChange}
                onInputChange={handleTrainerInput}
                onMenuOpen={() => {
                  if (!trainerOptions.length) loadTrainers();
                }}
                isLoading={loadingTrainers}
                placeholder="Select trainers..."
                noOptionsMessage={() => "No trainers found"}
                styles={selectStyles}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              />
              {formErrors.trainerIds && <div>{formErrors.trainerIds}</div>}
            </div>

            <div className="pg-field">
              <label className="pg-label">
                Batches
              </label>
              <Select
                isMulti
                options={batchOptions}
                value={selectedBatches}
                onChange={(selected) =>
                  setField("batchIds", (selected || []).map((o) => o.value))
                }
                onInputChange={handleBatchInput}
                isLoading={loadingBatches}
                isDisabled={!form.trainerIds.length}
                placeholder={form.trainerIds.length ? "Select batches..." : "Select trainers first"}
                noOptionsMessage={() => "No batches for the selected trainers"}
                styles={selectStyles}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              />
              {formErrors.batchIds && <div>{formErrors.batchIds}</div>}
            </div>

            <div className="pg-field">
              <label className="pg-label">Status</label>
              <select className="pg-select" value={form.status} onChange={(e) => setField("status", e.target.value)}>
                {statusOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="pg-field">
              <label className="pg-label">Priority</label>
              <select className="pg-select"
                value={form.priority}
                onChange={(e) => setField("priority", e.target.value)}
              >
                {priorityOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="pg-field">
              <label className="pg-label">Online link</label>
              <input
                className="pg-input"
                type="text"
                value={form.onlineLink}
                onChange={(e) => setField("onlineLink", e.target.value)}
                placeholder="Meeting URL (optional)"
              />
            </div>

            <div className="pg-field">
              <label className="pg-label">Venue / address</label>
              <input
                className="pg-input"
                type="text"
                value={form.offlineAddress}
                onChange={(e) => setField("offlineAddress", e.target.value)}
                placeholder="Room or address (optional)"
              />
            </div>

            <div className="pg-field">
              <label className="pg-label">
                Description
              </label>
              <input
                className="pg-input"
                rows={3}
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                placeholder="What this mock interview covers"
              />
              {formErrors.description && (
                <p className="att-error">{formErrors.description}</p>
              )}
            </div>
          </div>
        </div>
          )}

            </div>
        {!formDone && (
        <footer className="pg-modal-foot">
          <button
            type="button"
            className="pg-btn"
            onClick={closeForm}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="pg-btn pg-btn--main"
            onClick={handleSave}
            disabled={saving}
          >
            {saving && (
              <span className="pg-spinner att-spinner" aria-hidden="true" />
            )}
            {isEditing ? "Update interview" : "Create interview"}
          </button>
        </footer>
        )}
          </div>
        </div>
      )}

    </div>
  );
};

// ── Presentational helpers ──────────────────────────────────────────────────

// Turnout for one mock interview: how many of the enrolled students actually
// have an evaluation recorded against it. `undefined` means the stats call is
// still in flight; `expected === 0` means no batch is attached, so a ratio
// would be meaningless.
const PILL_TONE = {
  Active: "pg-pill--good",
  Inactive: "",
  High: "pg-pill--bad",
  Medium: "pg-pill--warn",
  Low: "pg-pill--good",
  "Not set": "",
};

const AttendedCell = ({ stat }) => {
  if (!stat) return <span className="lp-muted">—</span>;
  if (!stat.expected) {
    return (
      <span className="mi-fig">
        <span className="mi-fig-n">{stat.attended}</span>
        <span className="mi-fig-of">evaluated</span>
      </span>
    );
  }
  return (
    <span className="mi-fig" title={`${stat.attended} of ${stat.expected} attended`}>
      <span className="mi-fig-n">{stat.attended}</span>
      <span className="mi-fig-of">of {stat.expected}</span>
    </span>
  );
};

// Average score across every evaluation on the interview, banded by the same
// 50% threshold the evaluation modal uses to suggest Qualified.
const PERFORMANCE_BANDS = [
  { min: 75, tone: "is-strong", label: "Strong" },
  { min: 50, tone: "is-fair", label: "Fair" },
  { min: 0, tone: "is-weak", label: "Weak" },
];

const PerformanceCell = ({ stat }) => {
  if (!stat) return <span className="lp-muted">—</span>;
  if (stat.performanceRate === null) {
    return <span className="lp-muted">Not evaluated</span>;
  }
  const band = PERFORMANCE_BANDS.find((b) => stat.performanceRate >= b.min);
  return (
    <span
      className={`mi-score ${band.tone}`}
      title={`${band.label} — average across every evaluation`}
    >
      {stat.performanceRate}%
    </span>
  );
};

// Colour is a tag, not the subject: the tone comes from the meta the caller
// already resolved, as a tinted pill rather than a coloured figure.
const Pill = ({ meta }) => (
  <span className={`pg-pill ${PILL_TONE[meta?.label] || ""}`.trim()}>
    {meta?.label}
  </span>
);

// A mock interview can carry a dozen batches; showing them all would blow
// the column out, so two are shown and the rest are counted.
const NameList = ({ names }) => {
  if (!names?.length) return <span className="lp-muted">None</span>;
  return (
    <span className="lp-plain" title={names.join(", ")}>
      {names.slice(0, 2).join(", ")}
      {names.length > 2 && (
        <span className="lp-more">+{names.length - 2} more</span>
      )}
    </span>
  );
};

// ── Data helpers ────────────────────────────────────────────────────────────

// `date` and `time` come back as full ISO datetimes; the form needs them in
// the shapes <input type="date"> and <input type="time"> accept.
const toDateInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

// Times are stored as a UTC instant on 1970-01-01 (see AddEvent), so they're
// read back in UTC too — reading them locally would shift every row by the
// browser's offset.
const toTimeInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
};

const pad2 = (n) => String(n).padStart(2, "0");

const formatDateDisplay = (value) => {
  if (!value) return "N/A";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "N/A";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const formatTimeDisplay = (value) => {
  const hhmm = toTimeInput(value);
  if (!hhmm) return "N/A";
  const [h, m] = hhmm.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  return `${h % 12 === 0 ? 12 : h % 12}:${pad2(m)} ${suffix}`;
};

const formatDateTimeDisplay = (value) => {
  if (!value) return "N/A";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "N/A" : d.toLocaleString();
};

// The list endpoint replaces the CSV `trainerId`/`batchId` with the resolved
// rows (see attachRelations on the API), but a record whose ids no longer
// resolve keeps the raw string — handle both rather than rendering
// "[object Object]".
const toIdList = (value) => {
  if (Array.isArray(value)) return value.map((v) => String(v?.id || v)).filter(Boolean);
  if (typeof value === "string") {
    return value.split(",").map((v) => v.trim()).filter(Boolean);
  }
  return [];
};

const toNameList = (value, key) =>
  Array.isArray(value) ? value.map((v) => v?.[key]).filter(Boolean) : [];

// Appends a freshly-paged batch of options without re-adding anything the
// list already holds.
const mergeOptions = (existing, incoming) => {
  const seen = new Set(existing.map((o) => o.value));
  return [...existing, ...incoming.filter((o) => !seen.has(o.value))];
};

// Trainer and batch listings each report their pages a little differently;
// falling back to "a full page came back, so assume there's another" keeps the
// infinite scroll working whichever shape arrives.
const hasNextPage = (pagination, receivedCount) => {
  if (pagination) {
    if (typeof pagination.hasNextPage === "boolean") return pagination.hasNextPage;
    if (typeof pagination.hasNext === "boolean") return pagination.hasNext;
    const page = Number(pagination.page ?? pagination.currentPage) || 1;
    const totalPages = Number(pagination.totalPages) || 0;
    if (totalPages) return page < totalPages;
  }
  return receivedCount >= FILTER_PAGE_SIZE;
};

const cacheLabels = (cache, value, key) => {
  if (!Array.isArray(value)) return;
  value.forEach((v) => {
    if (v?.id) cache.set(String(v.id), v[key] || String(v.id));
  });
};

const toSelectValue = (ids, cache) =>
  (ids || []).map((id) => ({ value: String(id), label: cache.get(String(id)) || String(id) }));

// Legacy event rows store "N/A" in the optional text fields rather than null.
const cleanOptional = (value) => {
  const text = String(value ?? "").trim();
  return text && text !== "N/A" ? text : "";
};

// react-select renders its own DOM outside Bootstrap, so it has to be told the
// theme tokens explicitly — otherwise the menu stays white-on-white in the
// dark themes and the chips are unreadable in the light one.
const selectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "var(--card)",
    borderColor: state.isFocused ? "var(--saffron, #f1b07f)" : "var(--line)",
    boxShadow: "none",
    minHeight: 42,
    ":hover": { borderColor: "var(--saffron, #f1b07f)" },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  menu: (base) => ({
    ...base,
    backgroundColor: "var(--card)",
    border: "1px solid var(--line)",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "rgba(var(--ink-rgb), 0.08)" : "transparent",
    color: "var(--ink)",
    cursor: "pointer",
  }),
  multiValue: (base) => ({ ...base, backgroundColor: "rgba(var(--ink-rgb), 0.1)" }),
  multiValueLabel: (base) => ({ ...base, color: "var(--ink)" }),
  multiValueRemove: (base) => ({
    ...base,
    color: "var(--ink-2)",
    ":hover": { backgroundColor: "rgba(220, 53, 69, 0.15)", color: "#dc3545" },
  }),
  input: (base) => ({ ...base, color: "var(--ink)" }),
  singleValue: (base) => ({ ...base, color: "var(--ink)" }),
  placeholder: (base) => ({ ...base, color: "var(--ink-3, #9aa0a6)" }),
  noOptionsMessage: (base) => ({ ...base, color: "var(--ink-2)" }),
};

export default MockInterviews;
